import { type BaseNotificationItemProps, NotificationItem } from '../components/NotificationItem'
import { ShiftBadge } from '../components/ShiftBadge'

export type ShiftDemotionReason =
  | 'NO_SHOW'
  | 'MID_SHIFT_DISCONNECTION'
  | 'NO_TASK_ASSIGNED_IN_PERIOD'
  | 'AFK_OR_OFFLINE'

export type ShiftDemotionProps = BaseNotificationItemProps & {
  userName: string
  shiftId: string
  shiftStartDate: string
  shiftEndDate: string
  inactivityTime: string
  type: 'shift_demotion'
  reason: ShiftDemotionReason
  willBeReplaced: boolean
}

const getReasonDetails = (reason: ShiftDemotionReason) => {
  switch (reason) {
    case 'NO_SHOW':
      return {
        title: 'Due to No Show',
        description: 'did not start the shift within 5 minutes of start time.',
      }
    case 'MID_SHIFT_DISCONNECTION':
      return {
        title: 'Due to Disconnection',
        description: 'was offline for 10 minutes during the shift.',
      }
    case 'NO_TASK_ASSIGNED_IN_PERIOD':
      return {
        title: 'Due to No Tasks Completed',
        description: 'did not complete any tasks for 10 minutes and did not respond to the grace period.',
      }
    case 'AFK_OR_OFFLINE':
      return {
        title: 'Due to Inactivity',
        description: 'was inactive during the shift.',
      }
  }
}

export function ShiftDemotion({
  userName,
  shiftId,
  shiftStartDate,
  shiftEndDate,
  inactivityTime,
  reason,
  willBeReplaced,
  ...props
}: ShiftDemotionProps) {
  const reasonDetails = getReasonDetails(reason)
  const replacementMessage = willBeReplaced
    ? 'System will attempt to replace the shift'
    : 'No replacement shift will be created.'

  return (
    <NotificationItem
      {...props}
      title={`${userName} Shift Canceled ${reasonDetails.title}`}
      descriptionAddon={
        <div className='flex flex-col text-sm text-gray-500'>
          <p>
            {`${userName} ${reasonDetails.description}`} {replacementMessage}
          </p>
          <p className='mt-1'>
            Shift ID:{' '}
            <ShiftBadge
              id={shiftId}
              startDate={shiftStartDate}
              endDate={shiftEndDate}
              inactivityTime={inactivityTime}
              timezonePreference={props.timezonePreference}
            />
          </p>
        </div>
      }
    />
  )
}
